import React, { useContext, useEffect, useState } from 'react';
import { SCORE_WEIGHTAGE } from '../../utils/Constants';
import LockSwitch from '../../components/LockSwitch';
import { ABContext } from '../../contexts/ABContext';
import { TPayload, TScoreWeightage } from '../../types/ab';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import TextField from 'Components/DesignSystem/TextField/TextField';
import Typography from 'Components/DesignSystem/Typography/Typography';

const METRIC_OPTIONS = ['Revenue','Add To Cart Rate','Interactions Conversion Rate','Order Conversion Rate'];

const ScoreWeightage = () => {
  const abContext = useContext(ABContext);
  const [scoreWeightage, setScoreWeightage] = useState<TScoreWeightage[]>([]);

  useEffect(() => {
    if (abContext?.payload?.score_weightage && abContext.payload.score_weightage.length > 0) {
      setScoreWeightage(abContext.payload.score_weightage);
    }
  }, [abContext?.payload?.score_weightage]);

  useEffect(() => {
    if (abContext?.setPayload && scoreWeightage.length > 0) {
      abContext.setPayload((prevPayload: TPayload) => ({
        ...prevPayload,
        score_weightage: scoreWeightage
      }));
    }
  }, [scoreWeightage]);

  /*
    `(metric, checked)` rather than an event. The redistribution below — unlock a metric
    before removing it, then re-split the unlocked remainder — is unchanged; only the
    way the two values arrive has.
  */
  const handleMetricChange = (value: string, checked: boolean) => {
    if (!checked && scoreWeightage.find(sw => sw.metric_name === value)?.is_fixed) {
      toggleLock(value);
    }
    setScoreWeightage(prevWeightage => {
      const newWeightage = checked
        ? [...prevWeightage, { metric_name: value, value: 0, is_fixed: false }]
        : prevWeightage.filter(sw => sw.metric_name !== value);
      return updatePercentages(newWeightage);
    });
  };

  const toggleLock = (metric: string) => {
    setScoreWeightage(prevWeightage => {
      const newWeightage = prevWeightage.map(sw =>
        sw.metric_name === metric ? { ...sw, is_fixed: !sw.is_fixed } : sw
      );
      return updatePercentages(newWeightage);
    });
  };

  const updatePercentages = (weightage: TScoreWeightage[]): TScoreWeightage[] => {
    const lockedMetricsCount = weightage.filter(sw => sw.is_fixed).length;
    const totalLockedPercentage = weightage.reduce((sum, sw) => sw.is_fixed ? sum + sw.value : sum, 0);
    const remainingPercentage = 100 - totalLockedPercentage;
    const unlockedMetricsCount = weightage.length - lockedMetricsCount;
    const unlockedPercentage = unlockedMetricsCount > 0 ? remainingPercentage / unlockedMetricsCount : 0;

    return weightage.map(sw => ({
      ...sw,
      value: sw.is_fixed ? sw.value : unlockedPercentage
    }));
  };

  const isCheckboxDisabled = (metric: string): boolean => {
    const totalLockedPercentage = scoreWeightage
      .filter(sw => sw.is_fixed)
      .reduce((sum, sw) => sum + sw.value, 0);
    const isMetricSelected = scoreWeightage.some(sw => sw.metric_name === metric);

    return (
      scoreWeightage.some(sw => sw.is_fixed) &&
      !isMetricSelected &&
      totalLockedPercentage >= 100
    );
  };

  return (
    /*
      `surface-sunken` inside an `edge` border. It was `tw-bg-gray-100
      tw-border-zinc-300` — `zinc` is a stock Tailwind ramp that deep-merges into the
      config and compiles fine while being a colour this design system never chose.
    */
    <div className="tw-mb-3 tw-bg-surface-sunken tw-rounded-ctl tw-border tw-border-edge tw-p-4 tw-w-[95%]">
      <Typography variant="title" as="p" className="tw-text-ink-tertiary">
        {SCORE_WEIGHTAGE}
      </Typography>
      <div className="tw-grid tw-grid-cols-4 tw-gap-4 tw-mt-2">
        {METRIC_OPTIONS.map((metric) => {
          const metricWeightage = scoreWeightage.find(sw => sw.metric_name === metric);
          const isSelected = !!metricWeightage;
          const isLocked = metricWeightage?.is_fixed || false;
          const slug = metric.toLowerCase().replace(/\s+/g, '-');

          return (
            <div key={metric} className="tw-flex tw-flex-col tw-gap-2">
              {/*
                Both hooks here were hardcoded — `detail-scoreweightage-input-1` on all
                four checkboxes and `-2` on all four value boxes, so each matched four
                elements. Templated off the metric name now.
              */}
              <Checkbox
                label={metric}
                checked={isSelected}
                disabled={isCheckboxDisabled(metric)}
                onChange={(checked) => handleMetricChange(metric, checked)}
                data-testid={`detail-scoreweightage-check-${slug}`}
              />
              <div className='tw-flex tw-items-center tw-gap-2 tw-ml-6'>
                {/*
                  Read-only OUTPUT, not a field: it mirrors the split the lock buttons
                  drive and can never be typed into. It was `<input type="number"
                  readOnly>` — a tab stop announcing as an editable spinner. Same
                  reasoning as the traffic `%` box in § 137, except this one keeps its
                  border because it sits in a grid of four and the box is what aligns
                  them.
                */}
                <TextField
                  label=""
                  aria-label={`${metric} weighting`}
                  readOnly
                  tabIndex={-1}
                  type="text"
                  className="tw-w-[50%]"
                  value={metricWeightage?.value.toFixed(2) ?? ''}
                  placeholder="%"
                  data-testid={`detail-scoreweightage-value-${slug}`}
                />
                <LockSwitch
                  metric={metric}
                  isLocked={isLocked}
                  disabled={!isSelected || isCheckboxDisabled(metric)}
                  toggleLock={toggleLock}
                  data-testid={`detail-scoreweightage-lock-${slug}`}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ScoreWeightage;